import React, { useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { FiUpload } from "react-icons/fi";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { VscFilePdf } from "react-icons/vsc";
import { MdInsertDriveFile } from "react-icons/md";

import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import useStyles from "./styles";

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
const ALLOWED_EXTENSIONS = ["pdf", "doc", "docx", "jpg", "jpeg", "png"];

export interface UploadedAttachment {
  file: File;
  uri?: string;
  name: string;
  size: number;
  id?: string;
  fileUrl?: string;
}

interface AttachmentUploadProps {
  attachments: UploadedAttachment[];
  onAttachmentsChange: (attachments: UploadedAttachment[]) => void;
  disabled?: boolean;
}

const AttachmentUpload: React.FC<AttachmentUploadProps> = ({
  attachments,
  onAttachmentsChange,
  disabled = false,
}) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();

  const validateFile = (file: File): boolean => {
    const splittedFileName = file.name.split(".");
    const extension = splittedFileName[splittedFileName.length - 1]?.toLowerCase();

    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      NotificationToast.error(
        translate("thisFileExtensionIsNotAllowed") ||
          "This file extension is not allowed. Accepted formats: PDF, DOC, DOCX, JPG, PNG"
      );
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      NotificationToast.error(
        translate("fileIsTooBig") || "File is too big. Maximum file size is 25 MB"
      );
      return false;
    }

    return true;
  };

  const onDropFile = useCallback(
    async (files: File[]) => {
      if (!files || !files.length) return;

      const validFiles: File[] = [];
      for (const file of files) {
        if (validateFile(file)) {
          validFiles.push(file);
        }
      }

      if (validFiles.length === 0) return;

      const filePromises = validFiles.map((file) => {
        return new Promise<UploadedAttachment | null>((resolve) => {
          const fileId = `${Date.now()}_${Math.random()}`;
          const reader = new FileReader();

          reader.addEventListener("load", () => {
            const uri = reader.result?.toString();

            if (uri) {
              const attachment: UploadedAttachment = {
                file,
                uri,
                name: file.name,
                size: file.size,
                id: fileId,
              };
              resolve(attachment);
            } else {
              resolve(null);
            }
          });

          reader.addEventListener("error", () => {
            NotificationToast.error(translate("errorReadingFile") || "Error reading file");
            resolve(null);
          });

          reader.readAsDataURL(file);
        });
      });

      const results = await Promise.all(filePromises);
      
      const successfulAttachments = results.filter((att): att is UploadedAttachment => att !== null);
      
      if (successfulAttachments.length > 0) {
        onAttachmentsChange([...attachments, ...successfulAttachments]);
      }
    },
    [attachments, onAttachmentsChange, translate]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDropAccepted: onDropFile,
    accept: [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "image/jpeg",
      "image/png",
    ],
    disabled,
    multiple: true,
  });

  const handleRemoveFile = (id: string) => {
    onAttachmentsChange(attachments.filter((att) => att.id !== id));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split(".").pop()?.toLowerCase();
    if (extension === "pdf") {
      return <VscFilePdf size={16} className={classes.fileIcon} />;
    }
    return <MdInsertDriveFile size={16} className={classes.fileIcon} />;
  };

  return (
    <Box className={classes.container}>
      <Typography variant="h6" className={classes.sectionTitle}>
        {translate("userUploaded") || "User uploaded"}
      </Typography>

      <Box
        {...getRootProps()}
        className={`${classes.dropzone} ${isDragActive ? classes.dropzoneActive : ""} ${
          disabled ? classes.dropzoneDisabled : ""
        }`}
      >
        <input {...getInputProps()} />
        <Box className={classes.dropzoneContent}>
          <FiUpload size={24} className={classes.uploadIcon} />
          <Typography className={classes.uploadText}>
            {translate("uploadFiles") || "Upload files"}
          </Typography>
          <Typography className={classes.uploadSubtext}>
            {translate("orDragAndDropHere") || "or drag and drop here"}
          </Typography>
          <Typography className={classes.uploadInfo}>
            {translate("maxFileSizeLimit") || "Max file size limit is 25 MB. Accepted formats:"}{" "}
            <span className={classes.formatText}>PDF, DOC, DOCX, JPG, PNG</span>{" "}
            {translate("only") || "only"}
          </Typography>
        </Box>
      </Box>

      {attachments.length > 0 && (
        <Box className={classes.attachmentsList}>
          {attachments.map((attachment) => (
            <Box key={attachment.id} className={classes.attachmentChip}>
              <Box className={classes.attachmentChipLeft}>
                {getFileIcon(attachment.name)}
                <Typography className={classes.attachmentChipText}>
                  {attachment.name} ({formatFileSize(attachment.size)})
                </Typography>
              </Box>
              <Box
                className={classes.attachmentChipRemove}
                onClick={() => attachment.id && handleRemoveFile(attachment.id)}
              >
                <AiOutlineCloseCircle size={16} />
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default AttachmentUpload;

